import React, { useMemo } from "react"
import PropTypes from "prop-types"
import { Button, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { formatExperienceTermsFull } from "utils/experience"
import { getTicketedSlotsByDate } from "utils/experienceAvailability"
import { formatListPrice, getPriceSuffixKey } from "../helpers"
import { getTicketLowestPrice } from "./pricing"
import styles from "./WidgetExperience.module.css"

// The bar pinned to the bottom of the experience page below 992px (Figma 6058:142374): the
// experience's lowest price and length, and "Select Date", which opens the booking panel. A ticketed
// trip shows the cheapest ticket on its bookable dates, with the usual price struck through when a
// discount makes it lower ("From $100 $80 / person"). Private trips have no bookable dates before
// one is picked, so they show their lowest price only.
const PriceBar = ({ experience, onSelectDate }) => {
  const { t } = useTranslation()
  const priceSuffixKey = getPriceSuffixKey(experience)
  const duration = formatExperienceTermsFull(experience, t)
  const lowestPrice = useMemo(() => {
    if (experience?.experience_type !== "ticketed") return null
    return getTicketLowestPrice(experience, Object.keys(getTicketedSlotsByDate(experience)))
  }, [experience])
  const originalPrice = lowestPrice?.originalPrice

  return (
    <div className={styles.priceBar} data-price-bar="">
      <Flex align="center" gap={24}>
        <Flex vertical gap={4} className={styles.priceBarText}>
          <span className={styles.priceBarPrice}>
            {t("booking_widget.experience.price_bar.from")}{" "}
            {originalPrice != null && <span className={styles.priceBarStruck}>{formatListPrice(originalPrice)}</span>}
            {originalPrice != null && " "}
            {formatListPrice(originalPrice != null ? lowestPrice.price : experience?.min_price)}
            {priceSuffixKey && ` ${t(priceSuffixKey)}`}
          </span>
          {duration !== "—" && <span className={styles.priceBarDuration}>{t("booking_widget.experience.price_bar.for_duration", { duration })}</span>}
        </Flex>
        <Button type="primary" shape="round" className={styles.priceBarButton} onClick={onSelectDate}>
          {t("booking_widget.experience.select_date")}
        </Button>
      </Flex>
    </div>
  )
}

PriceBar.propTypes = {
  experience: PropTypes.shape({
    min_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    experience_type: PropTypes.string,
    pricing_type: PropTypes.string,
    ticket_types: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, price: PropTypes.number, enabled: PropTypes.bool })),
    time_slots: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, starts_at: PropTypes.string }))
  }).isRequired,
  onSelectDate: PropTypes.func.isRequired
}

export default PriceBar
